import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Globe, ChevronDown } from "lucide-react";
import { BRAND, NAV_LINKS, CATEGORIES } from "../lib/constants";
import { useLang } from "../lib/lang";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();

  // Close mobile menu on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 backdrop-blur-xl bg-[#0A0A0A] border-b border-white/10"
    >
      <div className="container-x flex items-center justify-between h-20 md:h-24">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
          <img
            src={`${BRAND.logoUrl}?w=480`}
            srcSet={`${BRAND.logoUrl}?w=480 480w, ${BRAND.logoUrl}?w=800 800w`}
            sizes="(max-width: 767px) 192px, 256px"
            alt="Theo-Oppolia"
            width="240"
            height="64"
            fetchPriority="high"
            decoding="async"
            className="h-12 md:h-16 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-8 xl:gap-10">
          {NAV_LINKS.map((l) => {
            // Products link gets a hover-dropdown of all categories
            if (l.path === "/products") {
              return (
                <div key={l.path} className="relative group" data-testid="nav-products-wrapper">
                  <NavLink
                    to={l.path}
                    data-testid={`nav-${l.name.toLowerCase()}`}
                    end={l.path === "/"}
                    className={({ isActive }) =>
                      `relative text-[12px] tracking-[0.18em] uppercase font-medium transition-colors flex items-center gap-1 ${
                        isActive ? "text-[#FF6A00]" : "text-[#F5F2EB] hover:text-[#FF6A00]"
                      }`
                    }
                  >
                    {lang === "fr" ? l.fr : l.name}
                    <ChevronDown size={12} className="opacity-70 group-hover:rotate-180 transition-transform" />
                  </NavLink>
                  {/* Dropdown — invisible until the wrapper is hovered/focused.
                      pt-3 provides a "hover bridge" so the menu does not flicker. */}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible focus-within:opacity-100 focus-within:visible transition-all duration-200 z-50">
                    <div className="min-w-[260px] bg-[#0A0A0A]/95 backdrop-blur-xl border border-white/10 shadow-2xl py-3" data-testid="nav-products-dropdown">
                      <Link
                        to="/products"
                        className="block px-5 py-2 text-[12px] tracking-[0.18em] uppercase font-medium text-[#F5F2EB] hover:text-[#FF6A00] hover:bg-white/5 transition"
                        data-testid="nav-products-dropdown-all"
                      >
                        All Collections
                      </Link>
                      <div className="h-px bg-white/10 my-2 mx-5" />
                      {CATEGORIES.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/products?cat=${encodeURIComponent(c.slug)}`}
                          className="block px-5 py-2 text-[12px] tracking-[0.18em] uppercase font-medium text-[#F5F2EB]/85 hover:text-[#FF6A00] hover:bg-white/5 transition"
                          data-testid={`nav-products-dropdown-${c.slug.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={`nav-${l.name.toLowerCase()}`}
                end={l.path === "/"}
                className={({ isActive }) =>
                  `relative text-[12px] tracking-[0.18em] uppercase font-medium transition-colors ${
                    isActive ? "text-[#FF6A00]" : "text-[#F5F2EB] hover:text-[#FF6A00]"
                  }`
                }
              >
                {lang === "fr" ? l.fr : l.name}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {/* Language toggle */}
          <div className="flex items-center gap-1 text-[11px] tracking-[0.2em] font-medium" data-testid="lang-toggle">
            <Globe size={12} className="text-[#FF6A00] mr-1" />
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-1.5 transition ${lang === "en" ? "text-[#FF6A00]" : "text-[#F5F2EB]/60 hover:text-[#F5F2EB]"}`}
            >EN</button>
            <span className="text-[#F5F2EB]/30">·</span>
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-1.5 transition ${lang === "fr" ? "text-[#FF6A00]" : "text-[#F5F2EB]/60 hover:text-[#F5F2EB]"}`}
            >FR</button>
          </div>
          <a
            href={`tel:${BRAND.phoneRaw}`}
            data-testid="phone-link"
            className="hidden xl:flex items-center gap-2 text-[#F5F2EB]/80 text-sm hover:text-[#FF6A00] transition"
          >
            <Phone size={14} /> {BRAND.phone}
          </a>
          <Link
            to="/consultation"
            data-testid="consultation-cta"
            className="btn-sharp primary"
          >
            {t("nav.consultation")}
          </Link>
        </div>

        <button
          aria-label="Open menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden text-[#F5F2EB]"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden overflow-hidden bg-[#0A0A0A] border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="container-x py-8 flex flex-col gap-5">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`mobile-nav-${l.name.toLowerCase()}`}
                  className="text-[#F5F2EB] text-lg font-display"
                >
                  {lang === "fr" ? l.fr : l.name}
                </Link>
              ))}
              <div className="flex items-center gap-2 text-[#F5F2EB]/70 text-sm pt-2">
                <Globe size={14} className="text-[#FF6A00]" />
                <button
                  onClick={() => setLang("en")}
                  className={lang === "en" ? "text-[#FF6A00]" : ""}
                  data-testid="mobile-lang-en"
                >EN</button>
                <span>·</span>
                <button
                  onClick={() => setLang("fr")}
                  className={lang === "fr" ? "text-[#FF6A00]" : ""}
                  data-testid="mobile-lang-fr"
                >FR</button>
              </div>
              <Link
                to="/consultation"
                className="btn-sharp primary justify-center"
                data-testid="mobile-consultation-cta"
              >
                {t("nav.consultation")}
              </Link>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="text-[#F5F2EB]/80 text-sm flex items-center gap-2"
              >
                <Phone size={14} /> {BRAND.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
